/**
 * SessionExpiredBar.jsx - "Your Microsoft sign-in needs refreshing." with a
 * Continue button, shown above the app when an account is cached but no token
 * can be minted (src/auth/session.js). Continue is the only way to redirect:
 * an automatic redirect could lose unsaved edits. Hidden again once a token is
 * minted; never shown when signed out (the login screen handles that).
 * Mounted once at the app root (src/index.js).
 */
import { useEffect, useState } from 'react';
import { msalInstance } from '../../auth/msalConfig';
import { SESSION_EXPIRED, SESSION_OK, isSessionExpired, watchSession, continueSignIn } from '../../auth/session';

export default function SessionExpiredBar() {
  const [expired, setExpired] = useState(isSessionExpired);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const on = () => setExpired(true);
    const off = () => setExpired(false);
    window.addEventListener(SESSION_EXPIRED, on);
    window.addEventListener(SESSION_OK, off);
    const unwatch = watchSession();
    setExpired(isSessionExpired()); // in case it changed before this effect ran
    return () => {
      window.removeEventListener(SESSION_EXPIRED, on);
      window.removeEventListener(SESSION_OK, off);
      unwatch();
    };
  }, []);

  if (!expired || !msalInstance.getAllAccounts()[0]) return null;

  const go = () => {
    setBusy(true);
    continueSignIn().catch((e) => {
      console.error('Sign-in redirect failed:', e);
      setBusy(false);
    });
  };

  return (
    <div
      role="alert"
      className="fixed inset-x-0 top-0 z-[1000] flex items-center justify-center gap-3 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-text-dark"
    >
      <span>Your Microsoft sign-in needs refreshing.</span>
      <button
        type="button"
        onClick={go}
        disabled={busy}
        className="rounded-md bg-gat-green px-3 py-1 font-medium text-white hover:bg-gat-greenDark focus:outline-none focus:ring-2 focus:ring-gat-green focus:ring-offset-2 disabled:opacity-60"
      >
        {busy ? 'Redirecting…' : 'Continue'}
      </button>
    </div>
  );
}
